import React from 'react';
import { Box, Divider, Grid2 } from '@mui/material';
import { useMode } from '../../context/Mode.context';
import { Mode } from '../../interfaces/Mode.interface';
import { ITemplateProps } from '../../interfaces/Template.interface';
import { ILayout, SectionName } from '../../interfaces/Theme.interface';
import { TemplateName } from '../../common/enum';
import { getSectionElements, getSections, ISectionElementsProps } from '../Template.util';

const DamascusTemplate = (props: ITemplateProps): React.ReactElement => {
    const { mode } = useMode();
    const isEditing: boolean = mode === Mode.EDIT;

    const templateName: TemplateName = TemplateName.DAMASCUS;

    const layout: ILayout = getSections(props.theme.layoutTheme, templateName);

    const showTopRightSection: boolean = layout.topRight.sections.some((i) => !!i);
    const showMiddleSection: boolean = layout.middleLeft.sections.some((i) => !!i) || layout.middleRight.sections.some((i) => !!i);
    const showBottomSection: boolean = layout.bottomLeft.sections.some((i) => !!i) || layout.bottomRight.sections.some((i) => !!i);

    const rightBottomDivider = (
        <Divider
            sx={{
                borderColor: props.theme.layoutTheme.borderColor,
                borderWidth: '1px',
                margin: '10px 0 10px -20px',
            }}
        />
    );

    const getProps = (sections: SectionName[]): ISectionElementsProps => ({
        templateName: templateName,
        sections: sections,
        templateProps: props,
        isEditing: isEditing,
    });

    return (
        <>
            {/* top */}
            <Grid2 container size={12}>
                {getSectionElements(getProps(layout.top.sections))}
            </Grid2>

            {/* top left/right */}
            <Grid2 container size={12}>
                <Grid2
                    size={isEditing ? 12 : showTopRightSection ? props.theme.layoutTheme.sectionWidth.topLeftWidth : 12}
                    alignItems="center"
                    alignContent="center"
                >
                    {getSectionElements(getProps(layout.topLeft.sections))}
                </Grid2>
                {showTopRightSection && (
                    <Grid2 size={isEditing ? 12 : 12 - props.theme.layoutTheme.sectionWidth.topLeftWidth}>
                        {getSectionElements(getProps(layout.topRight.sections))}
                    </Grid2>
                )}
            </Grid2>

            {/* middle */}
            <Grid2 container size={12}>
                {getSectionElements(getProps(layout.middle.sections))}
            </Grid2>

            {/* middle left/right */}
            {showMiddleSection && (
                <>
                    <Divider sx={{ borderColor: props.theme.layoutTheme.borderColor, borderWidth: '3px' }} />
                    <Grid2 container size={12}>
                        <Grid2
                            size={{
                                xs: isEditing ? 12 : props.theme.layoutTheme.sectionWidth.middleLeftWidth,
                                lg: props.theme.layoutTheme.sectionWidth.middleLeftWidth,
                            }}
                        >
                            <Box sx={{ padding: '15px 20px 0 0' }}>{getSectionElements(getProps(layout.middleLeft.sections))}</Box>
                        </Grid2>
                        <Grid2
                            size={{
                                xs: isEditing ? 12 : 12 - props.theme.layoutTheme.sectionWidth.middleLeftWidth,
                                lg: 12 - props.theme.layoutTheme.sectionWidth.middleLeftWidth,
                            }}
                        >
                            <Box
                                sx={{
                                    borderLeft: 'solid',
                                    borderColor: props.theme.layoutTheme.borderColor,
                                    borderWidth: '6px',
                                    padding: '15px 0 0 20px',
                                }}
                            >
                                {getSectionElements({ ...getProps(layout.middleRight.sections), extraBottomElement: rightBottomDivider })}
                            </Box>
                        </Grid2>
                    </Grid2>
                </>
            )}

            {/* bottom */}
            <Grid2 container size={12}>
                {getSectionElements(getProps(layout.bottom.sections))}
            </Grid2>
            {/* bottom left/right */}
            {showBottomSection && (
                <>
                    <Divider sx={{ borderColor: props.theme.layoutTheme.borderColor, borderWidth: '3px' }} />
                    <Grid2 container size={12}>
                        <Grid2
                            size={{
                                xs: isEditing ? 12 : props.theme.layoutTheme.sectionWidth.bottomLeftWidth,
                                lg: props.theme.layoutTheme.sectionWidth.bottomLeftWidth,
                            }}
                        >
                            <Box sx={{ padding: '15px 20px 0 0' }}>{getSectionElements(getProps(layout.bottomLeft.sections))}</Box>
                        </Grid2>
                        <Grid2
                            size={{
                                xs: isEditing ? 12 : 12 - props.theme.layoutTheme.sectionWidth.bottomLeftWidth,
                                lg: 12 - props.theme.layoutTheme.sectionWidth.bottomLeftWidth,
                            }}
                        >
                            <Box
                                sx={{
                                    borderLeft: 'solid',
                                    borderColor: props.theme.layoutTheme.borderColor,
                                    borderWidth: '6px',
                                    padding: '15px 0 0 20px',
                                }}
                            >
                                {getSectionElements({ ...getProps(layout.bottomRight.sections), extraBottomElement: rightBottomDivider })}
                            </Box>
                        </Grid2>
                    </Grid2>
                </>
            )}
        </>
    );
};

export default DamascusTemplate;
